import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { API, rupiah, imgSrc, errMsg } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag, TicketPercent, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, remove, subtotal, voucher, setVoucher, clearVoucher } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/masuk?next=/keranjang");
  }, [user, navigate]);

  const [available, setAvailable] = useState([]);
  useEffect(() => {
    if (user) API.get("/vouchers/available").then((r) => setAvailable(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!voucher?.code) return;
    const t = setTimeout(() => {
      const categories = items.map((i) => i.category || "");
      const qty_map = {};
      items.forEach((i) => { const c = i.category || ""; qty_map[c] = (qty_map[c] || 0) + i.qty; });
      const payload = voucher.vid
        ? { voucher_id: voucher.vid, subtotal, categories, qty_map }
        : { code: voucher.code, subtotal, categories, qty_map };
      API.post("/vouchers/validate", payload)
        .then(({ data }) => {
          if (data.discount !== voucher.discount) {
            setVoucher({ code: data.code || "", vid: data.vid || "", discount: data.discount, is_multiply: data.is_multiply, discount_type: data.discount_type });
          }
        })
        .catch(() => {
          clearVoucher();
          toast.error("Voucher dilepas karena tidak lagi memenuhi syarat untuk isi keranjang saat ini");
        });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, items]);

  const applyVoucher = async (preset, vid) => {
    const useCode = (typeof preset === "string" ? preset : code).trim();
    if (!vid && !useCode) return;
    setApplying(true);
    try {
      const categories = items.map((i) => i.category || "");
      const qty_map = {};
      items.forEach((i) => { const c = i.category || ""; qty_map[c] = (qty_map[c] || 0) + i.qty; });
      const payload = vid ? { voucher_id: vid, subtotal, categories, qty_map } : { code: useCode, subtotal, categories, qty_map };
      const { data } = await API.post("/vouchers/validate", payload);
      setVoucher({ code: data.code || "", vid: data.vid || "", discount: data.discount, is_multiply: data.is_multiply, discount_type: data.discount_type });
      toast.success(`Voucher dipakai: hemat ${rupiah(data.discount)}`);
      setCode("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 py-10" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <h1 className="font-display text-3xl tracking-tight mb-8">Keranjang Belanja</h1>
        {items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-14 text-center" data-testid="cart-empty">
            <ShoppingBag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-6">Keranjang Anda masih kosong.</p>
            <Link to="/toko" data-testid="cart-shop-link" className="inline-block px-8 py-3 bg-[#0B1F3A] bg-navy-hover text-white font-bold text-sm rounded-md transition-colors">
              Mulai Belanja
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {items.map((i) => (
                <div key={`${i.product_id}-${i.variant_label}-${i.unit}`} data-testid={`cart-item-${i.product_id}-${i.variant_label}-${i.unit}`} className="bg-white border border-slate-200 rounded-md p-4 flex gap-4 items-center">
                  <img src={imgSrc(i.image)} alt={i.name} className="h-20 w-20 rounded-md object-cover bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{i.name}</p>
                    <p className="text-xs text-slate-500">Varian: {i.variant_label} • {rupiah(i.price)}/{i.unit || "pcs"}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQty(i.product_id, i.variant_label, i.unit, i.qty - 1)} data-testid={`minus-${i.product_id}`} className="h-7 w-7 border border-slate-300 rounded flex items-center justify-center hover:border-[#0B1F3A]">
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={i.qty}
                        onChange={(e) => updateQty(i.product_id, i.variant_label, i.unit, Math.max(1, parseInt(e.target.value) || 1))}
                        data-testid={`qty-input-${i.product_id}`}
                        className="text-sm font-bold w-14 text-center border border-slate-300 rounded py-0.5 focus:outline-none focus:ring-2 focus:ring-[#C9A227] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button onClick={() => updateQty(i.product_id, i.variant_label, i.unit, i.qty + 1)} data-testid={`plus-${i.product_id}`} className="h-7 w-7 border border-slate-300 rounded flex items-center justify-center hover:border-[#0B1F3A]">
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm text-[#0B1F3A]">{rupiah(i.price * i.qty)}</p>
                    <button onClick={() => remove(i.product_id, i.variant_label, i.unit)} data-testid={`remove-${i.product_id}`} className="mt-2 text-red-500 hover:text-red-700 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-6 h-fit" data-testid="cart-summary">
              <h2 className="font-bold text-base mb-4">Ringkasan Belanja</h2>
              <div className="flex justify-between text-sm py-2 border-b border-slate-100">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-bold" data-testid="cart-subtotal">{rupiah(subtotal)}</span>
              </div>
              {voucher && (
                <div className="flex justify-between items-center text-sm py-2 border-b border-slate-100" data-testid="cart-voucher-row">
                  <span className="text-emerald-700 font-semibold">
                    Voucher {voucher.code || "Diskon"}
                    {voucher.is_multiply && (!voucher.discount_type || voucher.discount_type === "nominal") && <span className="block text-[10px] font-normal text-slate-400">Diskon berlipat — otomatis mengikuti jumlah barang</span>}
                    {voucher.discount_type === "kelipatan_min" && <span className="block text-[10px] font-normal text-slate-400">Diskon berkelipatan — otomatis mengikuti kelipatan min. pembelian</span>}
                    {voucher.discount_type === "persen" && <span className="block text-[10px] font-normal text-slate-400">Diskon persen — otomatis mengikuti subtotal belanja</span>}
                  </span>
                  <span className="font-bold text-emerald-700 flex items-center gap-2">
                    - {rupiah(voucher.discount)}
                    <button onClick={clearVoucher} data-testid="remove-voucher" className="text-red-500 hover:text-red-700"><X className="h-3.5 w-3.5" /></button>
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm py-2 border-b border-slate-100">
                <span className="text-slate-500">Ongkos Kirim</span>
                <span className="text-xs text-slate-400">Dihitung pemilik</span>
              </div>
              <div className="flex justify-between text-sm py-2 border-b border-slate-100 font-bold">
                <span>Total Sementara</span>
                <span data-testid="cart-total">{rupiah(Math.max(0, subtotal - (voucher?.discount || 0)))}</span>
              </div>
              {available.length > 0 && (
                <div className="mt-4" data-testid="voucher-list">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Voucher Tersedia untuk Anda</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {available.map((v) => {
                      const catOk = v.category === "semua" || items.some((i) => (i.category || "") === v.category);
                      const minOk = subtotal >= (v.min_purchase || 0);
                      const eligible = catOk && minOk;
                      const kurang = Math.max(0, (v.min_purchase || 0) - subtotal);
                      const benefit = v.discount_type === "persen"
                        ? `Diskon ${v.discount}% dari subtotal`
                        : `Diskon ${rupiah(v.discount)}${v.discount_type === "kelipatan_min" ? " × kelipatan min. pembelian" : v.is_multiply ? " × jumlah item" : ""}`;
                      return (
                        <div key={v.id} data-testid={`voucher-option-${v.id}`} className={`flex items-center justify-between gap-2 border rounded-md p-3 ${eligible ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-slate-50 opacity-70"}`}>
                          <div>
                            <p className="font-bold text-xs text-[#0B1F3A]">Voucher Diskon</p>
                            <p className="text-xs text-slate-600">{benefit}</p>
                            <p className="text-[10px] text-slate-400">
                              {v.min_purchase > 0
                                ? minOk
                                  ? `Min. pembelian ${rupiah(v.min_purchase)} ✓`
                                  : `Min. pembelian ${rupiah(v.min_purchase)} — kurang ${rupiah(kurang)} lagi`
                                : "Tanpa min. pembelian"}
                              {!catOk && ` • Khusus kategori ${v.category}`}
                            </p>
                          </div>
                          <button
                            onClick={() => applyVoucher(null, v.id)}
                            disabled={!eligible || applying}
                            data-testid={`voucher-use-${v.id}`}
                            className="shrink-0 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Pakai
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="mt-4">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Kode Voucher Diskon</label>
                <div className="flex gap-2">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    data-testid="voucher-input"
                    placeholder="Masukkan kode unik voucher"
                    className="flex-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  />
                  <button
                    onClick={() => applyVoucher()}
                    disabled={applying || !code.trim()}
                    data-testid="apply-voucher"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-xs font-bold rounded-md transition-colors disabled:opacity-50"
                  >
                    <TicketPercent className="h-3.5 w-3.5" /> Pakai
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">Kode voucher hanya dibagikan pemilik kepada pelanggan tertentu.</p>
              </div>
              <p className="text-xs text-slate-400 mt-3">Ongkir akan diinfokan oleh pemilik sebelum Anda membayar.</p>
              <Link
                to="/checkout"
                data-testid="checkout-button"
                className="mt-6 block text-center px-6 py-3.5 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors"
              >
                Lanjut ke Checkout
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
